/** Container load-planner contracts (Phase 11): vehicle sizes, plans and warnings. */

export interface VehicleType {
  id: number;
  code: string;
  name: string;
  vehicle_class: string;
  body_type: string;
  internal_length_mm: number;
  internal_width_mm: number;
  internal_height_mm: number;
  is_double_deck: boolean;
  deck2_clearance_mm: number | null;
  max_payload_kg: number | null;
  nominal_pallet_count: number | null;
  loads_from_rear: boolean;
  loads_from_left: boolean;
  loads_from_right: boolean;
  is_active: boolean;
  sort_order: number;
}

export interface PalletType {
  id: number;
  code: string;
  name: string;
  length_mm: number;
  width_mm: number;
  default_height_mm: number | null;
  default_weight_kg: number | null;
  is_stackable: boolean;
  is_active: boolean;
  sort_order: number;
}

export interface HaulierVehicleTypeAssignment {
  id: number;
  vehicle_type_id: number;
  is_preferred: boolean;
  sort_order: number;
  vehicle_type: VehicleType;
}

export interface LoadPlanWarning {
  code: string;
  severity: "error" | "warning";
  message_key: string;
  params: Record<string, unknown>;
  scope: string;
  bay_id: number | null;
  load_id: number | null;
  item_id: number | null;
}

export interface LoadPlanItem {
  id: number;
  collection_id: number | null;
  stop_id: number | null;
  pallet_type_id: number;
  length_mm: number;
  width_mm: number;
  height_mm: number;
  weight_kg: number | null;
  pos_x_mm: number;
  pos_y_mm: number;
  deck: number;
  orientation: number;
  load_sequence: number;
  stack_group: number | null;
  site_color: string | null;
  site_name: string | null;
  stop_sequence: number | null;
  load_ref: string | null;
}

export interface LoadPlan {
  id: number;
  load_id: number;
  load_ref: string | null;
  status: string;
  is_double_stack: boolean;
  row_version: number;
  vehicle_type: VehicleType;
  items: LoadPlanItem[];
  warnings: LoadPlanWarning[];
  generated_at: string | null;
  generated_by: number | null;
}

export interface DayPlanSummary {
  load_id: number;
  load_ref: string;
  plan_id: number | null;
  status: string | null;
  has_error: boolean;
  error_codes: string[];
  warnings: LoadPlanWarning[];
}

const qs = (params: Record<string, string | number | undefined>): string => {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) search.set(key, String(value));
  }
  const text = search.toString();
  return text ? `?${text}` : "";
};

export async function listVehicleTypes(includeInactive = false): Promise<VehicleType[]> {
  const { apiFetch } = await import("./client");
  const qs = includeInactive ? "" : "?include_inactive=true";
  return apiFetch<{ items: VehicleType[] }>(`/vehicle-types${qs}`).then((page) => page.items);
}

export async function listPalletTypes(activeOnly = true): Promise<PalletType[]> {
  const { apiFetch } = await import("./client");
  const qs = activeOnly ? "?include_inactive=false" : "";
  return apiFetch<{ items: PalletType[] }>(`/pallet-types${qs}`).then((page) => page.items);
}

export async function listHaulierVehicleTypes(haulierId: number): Promise<HaulierVehicleTypeAssignment[]> {
  const { apiFetch } = await import("./client");
  return apiFetch<HaulierVehicleTypeAssignment[]>(`/hauliers/${haulierId}/vehicle-types`);
}

export interface VehicleTypePayload {
  code: string;
  name: string;
  vehicle_class: string;
  body_type: string;
  internal_length_mm: number;
  internal_width_mm: number;
  internal_height_mm: number;
  is_double_deck: boolean;
  deck2_clearance_mm: number | null;
  max_payload_kg: number | null;
  nominal_pallet_count: number | null;
  loads_from_rear: boolean;
  loads_from_left: boolean;
  loads_from_right: boolean;
  is_active: boolean;
  sort_order: number;
}

export async function getVehicleType(vehicleTypeId: number): Promise<VehicleType> {
  const { apiFetch } = await import("./client");
  return apiFetch<VehicleType>(`/vehicle-types/${vehicleTypeId}`);
}

export async function createVehicleType(payload: VehicleTypePayload): Promise<VehicleType> {
  const { apiFetch } = await import("./client");
  return apiFetch<VehicleType>("/vehicle-types", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function updateVehicleType(
  vehicleTypeId: number,
  payload: Partial<VehicleTypePayload>,
): Promise<VehicleType> {
  const { apiFetch } = await import("./client");
  return apiFetch<VehicleType>(`/vehicle-types/${vehicleTypeId}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export interface HaulierVehicleTypeReplacePayload {
  vehicle_type_id: number;
  is_preferred: boolean;
  sort_order: number;
}

/** Replace the haulier's whole ordered vehicle-size assignment list. */
export async function replaceHaulierVehicleTypes(
  haulierId: number,
  assignments: HaulierVehicleTypeReplacePayload[],
): Promise<HaulierVehicleTypeAssignment[]> {
  const { apiFetch } = await import("./client");
  return apiFetch<HaulierVehicleTypeAssignment[]>(`/hauliers/${haulierId}/vehicle-types`, {
    method: "PUT",
    body: JSON.stringify({ assignments }),
  });
}

export async function plansForDay(day: string): Promise<DayPlanSummary[]> {
  const { apiFetch } = await import("./client");
  return apiFetch<DayPlanSummary[]>(`/load-plans/for-day${qs({ day })}`);
}

export async function getLoadPlan(loadId: number): Promise<LoadPlan> {
  const { apiFetch } = await import("./client");
  return apiFetch<LoadPlan>(`/load-plans${qs({ load_id: loadId })}`);
}

export interface GeneratePlanPayload {
  load_id: number;
  vehicle_type_id?: number | null;
  is_double_stack?: boolean | null;
}

export async function generateLoadPlan(payload: GeneratePlanPayload): Promise<LoadPlan> {
  const { apiFetch } = await import("./client");
  return apiFetch<LoadPlan>("/load-plans/generate", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function regenerateLoadPlan(
  planId: number,
  payload: GeneratePlanPayload,
): Promise<LoadPlan> {
  const { apiFetch } = await import("./client");
  return apiFetch<LoadPlan>(`/load-plans/${planId}/regenerate`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export interface MovePlanItemPayload {
  pos_x_mm: number;
  pos_y_mm: number;
  deck: number;
  orientation: number;
  row_version: number;
}

export interface MovePlanItemResult {
  id: number;
  pos_x_mm: number;
  pos_y_mm: number;
  deck: number;
  orientation: number;
  row_version: number;
}

export async function movePlanItem(
  planId: number,
  itemId: number,
  payload: MovePlanItemPayload,
): Promise<MovePlanItemResult> {
  const { apiFetch } = await import("./client");
  return apiFetch<MovePlanItemResult>(`/load-plans/${planId}/items/${itemId}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export async function planWarnings(planId: number): Promise<{ load_id: number; warnings: LoadPlanWarning[] }> {
  const { apiFetch } = await import("./client");
  return apiFetch<{ load_id: number; warnings: LoadPlanWarning[] }>(`/load-plans/${planId}/warnings`);
}
